import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Briefcase, GraduationCap, HeartPulse, Wifi, Dumbbell, PiggyBank } from 'lucide-react';
import PageHero from '../../components/PageHero';
import CTASection from '../../components/CTASection';
import { LINKS } from '../../config/siteContent';
import useDocumentTitle from '../../hooks/useDocumentTitle';

/**
 * Careers - content sourced from reference site careers.html.
 * No open positions at the moment; the page invites speculative resumes for
 * future roles and showcases the benefits package.
 */
const Careers = () => {
  useDocumentTitle('Careers');

  const benefits = [
    { Icon: HeartPulse, label: 'Health insurance', detail: 'Priority Health' },
    { Icon: PiggyBank, label: '401(k)', detail: '200% company match' },
    { Icon: GraduationCap, label: 'FSA', detail: 'Flexible Spending Account' },
    { Icon: HeartPulse, label: 'Dental insurance', detail: 'Full coverage' },
    { Icon: HeartPulse, label: 'Disability + Life insurance', detail: 'Short and long term' },
    { Icon: Dumbbell, label: 'On-site gym', detail: 'Free for employees' },
    { Icon: Wifi, label: 'Free internet', detail: 'On-campus' },
    { Icon: Briefcase, label: 'Stable team', detail: 'Low turnover, long tenure' },
  ];

  return (
    <>
      <PageHero
        eyebrow="Careers"
        title="Build the rails"
        titleAccent="of modern commerce."
        breadcrumbs={[
          { label: 'About', to: '/about' },
          { label: 'Careers' },
        ]}
        subhead="At iConnect, our success is based on employees who share a commitment to our customers and to each other. We're always interested in great people."
      >
        <a
          href={LINKS.careersApply}
          className="btn-primary"
        >
          <Mail size={16} />
          Email your resume
        </a>
      </PageHero>

      <section className="py-20 lg:py-24 bg-bg-canvas">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          {/* "No openings right now" card - keep the door open for speculative resumes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="card-elevated p-8 lg:p-12 mb-12 text-center"
          >
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="badge-sky">Hiring status</span>
            </div>
            <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-text-primary mb-4 tracking-[-0.02em]">
              No open positions right now.
            </h2>
            <p className="text-text-secondary leading-relaxed max-w-2xl mx-auto mb-6">
              We hire selectively when the right person and the right role line up.
              If iConnect sounds like a place you'd thrive, send us your resume - we
              keep great profiles on file and reach out the moment something opens.
            </p>
            <a href={LINKS.careersApply} className="btn-primary">
              <Mail size={16} />
              Send us your resume
            </a>
          </motion.div>

          {/* Benefits */}
          <div className="text-center mb-10">
            <p className="eyebrow">Benefits</p>
            <h2 className="font-display font-bold text-3xl lg:text-4xl text-text-primary tracking-[-0.02em] text-balance">
              <span className="text-gradient-sky">Take</span>{' '}
              care of yourself. We've got the rest.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {benefits.map((b, i) => (
              <motion.div
                key={b.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="card-elevated p-5"
              >
                <div className="w-10 h-10 rounded-lg bg-accent-soft flex items-center justify-center mb-3">
                  <b.Icon size={18} className="text-accent-primary" />
                </div>
                <div className="font-display font-bold text-sm text-text-primary mb-1">
                  {b.label}
                </div>
                <div className="text-xs text-text-muted">{b.detail}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default Careers;
